import {
  REVENUE_LEDGER_BUCKET_LABELS,
  summarizeClosingDiffs,
  type RevenueClosing,
  type RevenueLedgerMonthlyResponse,
} from '@cortex/domain-ac-mens-hub-api';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatYen } from '../../../lib/revenue-aggregation.js';
import {
  amountOf,
  buildLedgerTree,
  type LedgerTaxMode,
  type LedgerTreeNode,
} from '../../../lib/revenue-ledger-view.js';
import { ChartFilterBar } from '../../chart/ChartFilterBar.js';
import { CloseMonthModal, isReclose } from './CloseMonthModal.js';

/**
 * セルの金額の種類の選択肢。labelKey は描画側で t() する翻訳キー。
 * @graph-connects none
 */
const TAX_MODE_OPTIONS: readonly { key: LedgerTaxMode; labelKey: string }[] = [
  { key: 'net', labelKey: 'revenue.ledger.taxNet' },
  { key: 'tax', labelKey: 'revenue.ledger.taxTax' },
  { key: 'incl', labelKey: 'revenue.ledger.taxIncl' },
];

/** 締めの状態ごとのバッジの色。 @graph-connects none */
const BADGE_CLASS: Record<RevenueClosing['state'], string> = {
  open: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]',
  closed: 'bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)]',
  closed_with_diff: 'bg-[var(--color-error-muted)] text-[var(--color-error)]',
  version_mismatch: 'bg-[var(--color-warning-muted)] text-[var(--color-text-primary)]',
};

/**
 * 締めの状態の取得の状況。読み込み中・失敗の間は締めの状態を空として扱わない
 * （空に落とすと締め済みの月に「この月を締める」が出てしまう）。
 * @graph-connects none
 */
export type LedgerClosingsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; closings: readonly RevenueClosing[] };

/** 符号つきの金額。 @graph-connects none */
function signedYen(value: number): string {
  return `${value > 0 ? '+' : ''}${formatYen(value)}`;
}

/**
 * 本文の行の背景。左に固定した勘定の列は横スクロールで下を通る値を隠すため背景を塗る必要があり、
 * 行と同じ色・同じホバーの色にして、固定の列だけ色が違って見えないようにする（グループの行も同じ）。
 * @graph-connects none
 */
const LEDGER_ROW_CLASS = 'group bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-elevated)]';

/** 左に固定した勘定の列の背景（行の背景とホバーに合わせる）。 @graph-connects none */
const LEDGER_STICKY_CELL_CLASS =
  'bg-[var(--color-bg-surface)] group-hover:bg-[var(--color-bg-elevated)]';

/** 値の無いマス（その月に行が無い・その版が持たない値）は – にする。 @graph-connects none */
function formatCell(value: number | null): string {
  return value === null ? '–' : value.toLocaleString('ja-JP');
}

/** @graph-connects none */
function closedDate(iso: string | null): string {
  return iso === null
    ? ''
    : new Date(iso).toLocaleDateString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        month: 'numeric',
        day: 'numeric',
      });
}

/**
 * 月の見出しに出す締めの状態のバッジ（未締め・締め済み・締め後に差あり・版違い）。
 * @graph-connects none
 */
function ClosingBadge({ closing }: { closing: RevenueClosing }) {
  const { t } = useTranslation();
  let text: string;
  switch (closing.state) {
    case 'open':
      text = t('revenue.ledger.stateOpen');
      break;
    case 'closed':
      text = t('revenue.ledger.stateClosed', {
        date: closedDate(closing.closedAt),
        by: closing.closedBy,
      });
      break;
    case 'closed_with_diff':
      text =
        closing.diffs === null
          ? t('revenue.ledger.stateDiffUnknown')
          : t('revenue.ledger.stateClosedWithDiff');
      break;
    case 'version_mismatch':
      text = t('revenue.ledger.stateVersionMismatch', {
        closed: closing.closedVersion,
        current: 'v0.1',
      });
      break;
  }
  return (
    <div
      className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] ${BADGE_CLASS[closing.state]}`}
    >
      {text}
      {summarizeClosingDiffs(closing.diffs ?? []).map((d) => (
        <div key={d.bucket} className="tabular-nums">
          {t('revenue.ledger.diffLine', {
            bucket: REVENUE_LEDGER_BUCKET_LABELS[d.bucket],
            net: signedYen(d.amountNet),
            tax: signedYen(d.consumptionTax),
            incl: signedYen(d.amountInclTax),
            cells: String(d.cells),
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（正式版）の月次タブ。行は勘定＞プラン＞内訳の折りたたみ、列は月、セルは税抜・税・税込を切替。締め済みの月は締めた朝の計算の値を出し、各月の見出しに締めの状態（締め後に差があれば勘定ごとの税抜・税・税込の差と変わったマスの数）と、締めの権限がある人にだけ「この月を締める」を出す。締めるボタンは、締めの状態を読めていて、API が締められると返した月（月末を過ぎた月）だけに出す。
 * @graph-connects buildLedgerTree [calls] 折りたたみ表の組み立て
 * @graph-connects CloseMonthModal [calls] 締めの確認モーダル
 */
export function LedgerMonthlyView({
  ledger,
  closingsState,
  canClose,
  includeWithdrawal,
}: {
  ledger: RevenueLedgerMonthlyResponse;
  closingsState: LedgerClosingsState;
  canClose: boolean;
  includeWithdrawal: boolean;
}) {
  const { t } = useTranslation();
  const [taxMode, setTaxMode] = useState<LedgerTaxMode>('net');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [closingMonth, setClosingMonth] = useState<string | null>(null);
  const tree = useMemo(
    () => buildLedgerTree(ledger.rows, includeWithdrawal),
    [ledger.rows, includeWithdrawal],
  );
  const closings = closingsState.status === 'ready' ? closingsState.closings : null;
  const closingByMonth = useMemo(
    () => new Map((closings ?? []).map((c) => [c.month, c])),
    [closings],
  );
  // 締めはその月の最新の計算を確定値にする（締め済み月の表の値は締めた計算なので、表の値とは別に持つ）
  const latestRunByMonth = useMemo(
    () => new Map(ledger.latestRuns.map((r) => [r.month, r])),
    [ledger.latestRuns],
  );
  const hasClosedMonth = (closings ?? []).some(
    (c) => c.state === 'closed' || c.state === 'closed_with_diff',
  );
  const taxOptions = TAX_MODE_OPTIONS.map((o) => ({ key: o.key, label: t(o.labelKey) }));

  /** @graph-connects none */
  function toggle(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** @graph-connects none */
  function renderNode(node: LedgerTreeNode, depth: number): ReactNode {
    const isOpen = !collapsed.has(node.id);
    const hasChildren = node.children.length > 0;
    return (
      <Fragment key={node.id}>
        <tr className={`${LEDGER_ROW_CLASS} ${node.excludedFromTotal ? 'opacity-40' : ''}`}>
          <td
            className={`sticky left-0 z-10 border-b border-[var(--color-border)] ${LEDGER_STICKY_CELL_CLASS} px-3 py-1.5 text-left whitespace-nowrap`}
            style={{ paddingLeft: `${String(12 + depth * 16)}px` }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => {
                  toggle(node.id);
                }}
                aria-label={t(isOpen ? 'revenue.ledger.collapse' : 'revenue.ledger.expand', {
                  name: node.label,
                })}
                aria-expanded={isOpen}
                className={`inline-flex items-center gap-1 ${depth === 0 ? 'font-semibold' : 'font-medium'} text-[var(--color-text-primary)]`}
              >
                {isOpen ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {node.label}
              </button>
            ) : (
              <span className="text-[var(--color-text-secondary)]">{node.label}</span>
            )}
          </td>
          {ledger.months.map((month) => {
            const amounts = node.amountsByMonth.get(month);
            return (
              <td
                key={month}
                className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]"
              >
                {formatCell(amounts === undefined ? null : amountOf(amounts, taxMode))}
              </td>
            );
          })}
        </tr>
        {hasChildren && isOpen && node.children.map((child) => renderNode(child, depth + 1))}
      </Fragment>
    );
  }

  const closingTarget = closingMonth === null ? undefined : closingByMonth.get(closingMonth);
  const closingRun = closingMonth === null ? undefined : latestRunByMonth.get(closingMonth);

  return (
    <div className="mt-6 space-y-3">
      <ChartFilterBar
        label={t('revenue.ledger.cellLabel')}
        options={taxOptions}
        value={taxMode}
        onChange={setTaxMode}
      />
      {closingsState.status === 'loading' && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('revenue.ledger.closingsLoading')}
        </p>
      )}
      {closingsState.status === 'error' && (
        <p role="alert" className="text-xs text-[var(--color-error)]">
          {t('revenue.ledger.closingsError')}
        </p>
      )}
      {hasClosedMonth && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('revenue.ledger.closedValueNote')}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
        <table className="min-w-full border-collapse text-right text-sm">
          <thead>
            <tr className="bg-[var(--color-bg-base)] align-top">
              <th className="sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-left font-semibold text-[var(--color-text-secondary)]">
                {t('revenue.ledger.accountHeader')}
              </th>
              {ledger.months.map((month) => {
                const closing = closingByMonth.get(month);
                return (
                  <th
                    key={month}
                    className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]"
                  >
                    <div className="font-mono text-[var(--color-text-primary)]">{month}</div>
                    {closing !== undefined && <ClosingBadge closing={closing} />}
                    {closings !== null &&
                      canClose &&
                      latestRunByMonth.get(month)?.closable === true && (
                        <div className="mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setClosingMonth(month);
                            }}
                            className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-hover)]"
                          >
                            {t(
                              isReclose(closing)
                                ? 'revenue.ledger.recloseButton'
                                : 'revenue.ledger.closeButton',
                            )}
                          </button>
                        </div>
                      )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{tree.map((node) => renderNode(node, 0))}</tbody>
        </table>
      </div>
      {closingMonth !== null && closingRun !== undefined && (
        <CloseMonthModal
          month={closingMonth}
          closing={closingTarget}
          run={closingRun}
          onClose={() => {
            setClosingMonth(null);
          }}
        />
      )}
    </div>
  );
}
